/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import fr from '../locales/fr.json'

const LANGUAGE_STORAGE_KEY = 'ng-language'

const localeLoaders = {
  fr: async () => fr,
  en: async () => (await import('../locales/en.json')).default,
  es: async () => (await import('../locales/es.json')).default,
  it: async () => (await import('../locales/it.json')).default,
  de: async () => (await import('../locales/de.json')).default,
  zh: async () => (await import('../locales/zh.json')).default,
  ar: async () => (await import('../locales/ar.json')).default,
}

const LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'de', label: 'Deutsch' },
  { code: 'zh', label: '中文' },
  { code: 'ar', label: 'العربية' },
]

const LanguageContext = createContext(null)

const getLanguageLabel = (code) => LANGUAGE_OPTIONS.find((option) => option.code === code)?.label || 'Français'
const getPathValue = (source, path) => path.split('.').reduce((result, key) => result?.[key], source)

export function LanguageProvider({ children }) {
  const [messages, setMessages] = useState({ fr })
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return 'fr'
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'fr'
  })

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
    document.documentElement.lang = language
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr'
  }, [language])

  useEffect(() => {
    let isMounted = true

    if (messages[language]) return undefined

    const loadLocale = localeLoaders[language] || localeLoaders.fr

    loadLocale()
      .then((loadedMessages) => {
        if (!isMounted) return
        setMessages((current) => ({ ...current, [language]: loadedMessages }))
      })
      .catch(() => {
        if (!isMounted) return
        setMessages((current) => ({ ...current, [language]: current.fr || fr }))
      })

    return () => {
      isMounted = false
    }
  }, [language, messages])

  const value = useMemo(() => {
    const translation = messages[language] || messages.fr || fr

    return {
      language,
      languageLabel: getLanguageLabel(language),
      languageOptions: LANGUAGE_OPTIONS,
      setLanguage,
      t: (path) => getPathValue(translation, path) ?? getPathValue(messages.fr || fr, path) ?? path,
    }
  }, [language, messages])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }

  return context
}